import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "@/i18n";

// New logo with transparent background
const LOGO_IMAGE = "https://customer-assets.emergentagent.com/job_export-to-wp/artifacts/lkrjqebc_Logo%20Spurna.png";

// Navigation items with paths for both languages
const getNavItems = (lang, t) => {
  if (lang === 'en') {
    return [
      { href: "/en/philosophy", label: t.nav.filosofia },
      { href: "/en/projects", label: t.nav.proyectos },
      { href: "/en/advertising", label: t.nav.publicidad },
      { href: "/en/what-we-do", label: t.nav.queHacemos },
      { href: "/en/contact", label: t.nav.hablemos },
    ];
  }
  return [
    { href: "/filosofia", label: t.nav.filosofia },
    { href: "/proyectos", label: t.nav.proyectos },
    { href: "/publicidad", label: t.nav.publicidad },
    { href: "/que-hacemos", label: t.nav.queHacemos },
    { href: "/hablemos", label: t.nav.hablemos },
  ];
};

// Language switcher component
const LanguageSwitcher = () => {
  const { lang, getSwitchPath } = useLanguage();
  
  return (
    <div className="flex items-center gap-1 text-xs tracking-wider">
      <Link
        to={getSwitchPath('es')}
        className={cn(
          "px-2 py-1 transition-all duration-300",
          lang === 'es' 
            ? "text-header-text font-medium border-b border-header-text/50" 
            : "text-header-text/50 hover:text-header-text/70"
        )}
      >
        ES
      </Link>
      <span className="text-header-text/30">|</span>
      <Link
        to={getSwitchPath('en')}
        className={cn(
          "px-2 py-1 transition-all duration-300",
          lang === 'en' 
            ? "text-header-text font-medium border-b border-header-text/50" 
            : "text-header-text/50 hover:text-header-text/70"
        )}
      >
        EN
      </Link>
    </div>
  );
};

export const Header = ({ isTransparent = false }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { lang, t, isEnglish } = useLanguage();
  
  const navItems = getNavItems(lang, t);
  const homePath = isEnglish ? "/en" : "/";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const headerBg = isTransparent && !isScrolled 
    ? "bg-transparent" 
    : "bg-background/90 backdrop-blur-md";

  return (
    <motion.header
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-500",
        headerBg
      )}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <nav className="flex items-center h-20 lg:h-24">
          {/* Logo - transparent background, larger size, color matches nav text */}
          <Link 
            to={homePath} 
            className="relative z-10 flex items-center group shrink-0"
            aria-label="Spurna Creativa - Inicio"
          >
            <img
              src={LOGO_IMAGE}
              alt="Spurna Creativa Logo"
              className="h-14 w-auto sm:h-16 lg:h-20 xl:h-22 object-contain"
              style={{ 
                maxWidth: '220px',
                filter: 'brightness(0.85) contrast(1.1) sepia(0.1)',
                opacity: 0.7,
              }}
            />
          </Link>

          {/* Desktop Navigation - Same line, to the right of logo with larger font */}
          <div className="hidden lg:flex items-center gap-6 xl:gap-8 ml-8 xl:ml-12">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  "text-xs xl:text-sm font-medium tracking-[0.15em] text-header-text/70 hover:text-header-text transition-all duration-300 relative py-1",
                  location.pathname === item.href && "text-header-text"
                )}
              >
                {item.label}
                {location.pathname === item.href && (
                  <motion.div
                    layoutId="activeNav"
                    className="absolute -bottom-0.5 left-0 right-0 h-px bg-header-text/40"
                    transition={{ duration: 0.3 }}
                  />
                )}
              </Link>
            ))}
          </div>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Language Switcher - Desktop */}
          <div className="hidden lg:block mr-4">
            <LanguageSwitcher />
          </div>

          {/* Mobile Menu Button */}
          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button
                variant="ghost"
                size="icon"
                className="relative z-10 text-header-text/80 hover:text-header-text hover:bg-transparent"
                aria-label={t.common.openMenu}
              >
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent 
              side="right" 
              className="w-full sm:w-80 bg-background border-l border-border/30 p-0"
            >
              <SheetTitle className="sr-only">Navigation menu</SheetTitle>
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between p-6 border-b border-border/20">
                  <img
                    src={LOGO_IMAGE}
                    alt="Spurna Creativa Logo"
                    className="h-12 w-auto object-contain"
                    style={{ 
                      maxWidth: '160px',
                      filter: 'brightness(0.85) contrast(1.1) sepia(0.1)',
                      opacity: 0.7,
                    }}
                  />
                  {/* Language Switcher - Mobile */}
                  <LanguageSwitcher />
                </div>
                
                <nav className="flex flex-col p-6 gap-1">
                  <AnimatePresence>
                    {navItems.map((item, index) => (
                      <motion.div
                        key={item.href}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1, duration: 0.3 }}
                      >
                        <SheetClose asChild>
                          <Link
                            to={item.href}
                            className={cn(
                              "block py-4 text-base tracking-widest text-foreground/60 hover:text-foreground transition-colors duration-300 border-b border-border/10",
                              location.pathname === item.href && "text-foreground"
                            )}
                          >
                            {item.label}
                          </Link>
                        </SheetClose>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </nav>

                <div className="mt-auto p-6 border-t border-border/20">
                  <p className="text-xs text-muted-foreground tracking-wide">
                    {t.footer.audiovisualProduction}
                  </p>
                  <a 
                    href="https://www.spurnacreativa.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-xs text-foreground/50 hover:text-foreground transition-colors"
                  >
                    www.spurnacreativa.com
                  </a>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </nav>
      </div>
    </motion.header>
  );
};
